import type {
    IDictionary,
    TrackerEngine as ITrackerEngine,
    TrackEventPayload,
    TrackingEventListener,
} from './types';

declare global {
    interface Window {
        dataHub?: ITrackerEngine;
    }
}

import { appLogger } from '@/utils/app-logger/appLogger';
import { createConsoleLogger } from '@/utils/devtools/console-logger';

import { BootstrapSignalKeys, BootstrapSignals } from './bootstrap-signals';

const { log, error: logError } = createConsoleLogger('TrackerEngine:methodCalls');

const COMPONENT = 'TrackerEngine';

const DATA_HUB_WAIT_TIMEOUT_MS = 10_000;
const MAX_PENDING_UPDATE_EVENTS = 500;
const DATA_HUB_POLL_BACKOFF_AFTER_MS = 30_000;

/**
 * Why an update() event never reached dataHub. update() waiters never expire on a clock,
 * so a drop is always a queue-depth or environment decision — the remedies differ, and
 * every one of these looks identical ("no dataHub") from the call site.
 */
type DroppedEventReason =
    /** Shed at the queue cap so fresher session state could enqueue. */
    | 'queue-cap-evicted'
    /** Cap reached, but every pending event is pre-launch-ready and must not be shed. */
    | 'queue-cap-full'
    /** No `window` to wait on — update() reached during SSR. */
    | 'no-window';

export class TrackerEngine {
    /**
     * Safely checks if dataHub is available on window
     */
    private static isDataHubAvailable(): boolean {
        return typeof window !== 'undefined' && window.dataHub !== undefined;
    }

    private static readonly dataHubRetryIntervalMs = 50;
    // Non-expiring update() waiters can keep the poller alive for the whole session
    // when Tealium/DIT is blocked outright — back off to a coarse interval after the
    // initial load window so those tabs stop waking 20 times a second.
    private static readonly dataHubSlowRetryIntervalMs = 1_000;
    private static dataHubPollStartedMs: number | undefined;
    private static dataHubPollTimerId: number | undefined;
    private static dataHubWaiters: Array<{
        deadlineMs: number;
        resolve: (isAvailable: boolean) => void;
        // Only update() events enqueued after LAUNCH_READY may be shed at the cap;
        // the pre-launch-ready head (appLaunch carrying identity.fpid, consent and
        // info updates) must always survive to seed dataHub state.
        isEvictable: boolean;
    }> = [];

    /** Observability for the dataHub bootstrap race — see `logDataHubReady`/`logDroppedEvent`. */
    private static firstWaitStartedAtMs: number | undefined;
    private static hasLoggedDataHubReady = false;
    private static droppedEventCount = 0;

    /**
     * Records how long callers had to wait for utag.js to define `window.dataHub`. A long
     * wait here is the window in which events emit without app metadata, so surfacing it
     * makes the otherwise-invisible race measurable in production. Call from every path that
     * observes availability, including fast paths — when dataHub arrives after all waiters
     * timed out, polling has already stopped and this is the only remaining chance to log.
     */
    private static logDataHubReady(): void {
        if (this.hasLoggedDataHubReady || this.firstWaitStartedAtMs === undefined) return;
        this.hasLoggedDataHubReady = true;
        appLogger.info('dataHub became available after wait', {
            component: COMPONENT,
            waitedMs: Date.now() - this.firstWaitStartedAtMs,
        });
    }

    /** Logs the first dropped event only; later drops just increment the counter. */
    private static logDroppedEvent(eventType: string, reason: DroppedEventReason): void {
        this.droppedEventCount += 1;
        if (this.droppedEventCount > 1) return;
        appLogger.error('dataHub unavailable; dropping tracking events', {
            component: COMPONENT,
            reason,
            eventType,
        });
    }

    private static fireOnceOnTrigger = new Map<
        string,
        Map<string, (detail: IDictionary) => TrackEventPayload>
    >();

    /**
     * Register an event to fire alongside the next firing of an event matching triggerType.  The event builder
     * is passed the context of the trigger event to enrich the build the new event.
     * @param triggerType - The TrackingEvent.type that acts as the trigger
     * @param builder - The builder for the event to fire
     */
    static registerFireOnce(
        triggerType: string,
        builder: { type: string; build: (detail: IDictionary) => TrackEventPayload },
    ): void {
        let eventBuilders = this.fireOnceOnTrigger.get(triggerType);
        if (!eventBuilders) {
            eventBuilders = new Map();
            this.fireOnceOnTrigger.set(triggerType, eventBuilders);
        }
        eventBuilders.set(builder.type, builder.build);
    }

    private static _update(event: TrackEventPayload): void {
        try {
            window.dataHub!.update(event);
        } catch (error) {
            log('Error calling dataHub.update():', error);
        }
    }

    /**
     * Add events that are triggered by the target event to the event queue to be sent
     */
    private static drainFireOnce(event: TrackEventPayload): void {
        const eventBuilders = this.fireOnceOnTrigger.get(event.type);
        if (!eventBuilders || eventBuilders.size === 0) return;
        const builders = new Map(eventBuilders);
        eventBuilders.clear();
        this.fireOnceOnTrigger.delete(event.type);
        builders.forEach((build) => {
            this.update(build(event.detail));
        });
    }

    private static clearDataHubPolling(): void {
        if (this.dataHubPollTimerId !== undefined) {
            clearTimeout(this.dataHubPollTimerId);
            this.dataHubPollTimerId = undefined;
        }
    }

    private static startDataHubPolling(): void {
        if (this.dataHubPollTimerId !== undefined) return;
        if (typeof window === 'undefined') return;
        this.dataHubPollStartedMs = Date.now();

        const tick = () => {
            if (this.isDataHubAvailable()) {
                const waiters = this.dataHubWaiters;
                this.dataHubWaiters = [];
                this.clearDataHubPolling();
                this.logDataHubReady();
                for (const waiter of waiters) waiter.resolve(true);
                return;
            }

            const now = Date.now();
            const remaining: typeof this.dataHubWaiters = [];
            for (const waiter of this.dataHubWaiters) {
                if (now >= waiter.deadlineMs) {
                    waiter.resolve(false);
                } else {
                    remaining.push(waiter);
                }
            }
            this.dataHubWaiters = remaining;

            if (this.dataHubWaiters.length === 0) {
                this.clearDataHubPolling();
                return;
            }

            const elapsedMs = now - (this.dataHubPollStartedMs ?? now);
            const intervalMs =
                elapsedMs >= DATA_HUB_POLL_BACKOFF_AFTER_MS
                    ? this.dataHubSlowRetryIntervalMs
                    : this.dataHubRetryIntervalMs;
            this.dataHubPollTimerId = window.setTimeout(tick, intervalMs);
        };

        this.dataHubPollTimerId = window.setTimeout(tick, 0);
    }

    private static pendingUpdateWaiterCount(): number {
        return this.dataHubWaiters.filter((waiter) => waiter.deadlineMs === Infinity).length;
    }

    /**
     * Resolves and removes the oldest evictable update() waiter to make room at the
     * cap. Returns false when every pending waiter belongs to the protected
     * pre-launch-ready head (the caller then drops the incoming event instead).
     */
    private static evictOldestEvictableWaiter(): boolean {
        const index = this.dataHubWaiters.findIndex((waiter) => waiter.isEvictable);
        if (index === -1) return false;
        const [evicted] = this.dataHubWaiters.splice(index, 1);
        evicted.resolve(false);
        return true;
    }

    private static waitForDataHub(timeoutMs = DATA_HUB_WAIT_TIMEOUT_MS): Promise<boolean> {
        if (this.isDataHubAvailable()) return Promise.resolve(true);
        if (typeof window === 'undefined') return Promise.resolve(false);

        this.firstWaitStartedAtMs ??= Date.now();
        const deadlineMs = Date.now() + timeoutMs;
        // Infinity marks an update() waiter; those become evictable once the
        // bootstrap sequence has settled (LAUNCH_READY marks within ~15s even in
        // broken sessions via the BootstrapSignals whenAll fail-open timeout).
        const isEvictable =
            timeoutMs === Infinity && BootstrapSignals.has(BootstrapSignalKeys.LAUNCH_READY);
        return new Promise((resolve) => {
            this.dataHubWaiters.push({ deadlineMs, resolve, isEvictable });
            this.startDataHubPolling();
        });
    }

    /**
     * Register a listener for tracking events
     * @param name - Event name to listen for
     * @param listener - Callback function to execute when event occurs
     */
    static on(name: string, listener: TrackingEventListener): void {
        if (this.isDataHubAvailable()) {
            try {
                window.dataHub!.on(name, listener);
            } catch (error) {
                log('Error calling dataHub.on():', error);
            }
            return;
        }

        this.waitForDataHub(DATA_HUB_WAIT_TIMEOUT_MS).then((isAvailable) => {
            if (!isAvailable) {
                log('dataHub is not available on window. Skipping on() call.');
                return;
            }

            try {
                window.dataHub!.on(name, listener);
            } catch (error) {
                log('Error calling dataHub.on():', error);
            }
        });
    }

    /**
     * Update the tracker with a new event
     * @param event - The tracking event payload
     * @returns Promise that resolves once the update has been sent to dataHub
     */
    static async update(event: TrackEventPayload): Promise<void> {
        if (this.isDataHubAvailable()) {
            this.logDataHubReady();
            this.drainFireOnce(event);
            this._update(event);
            return;
        }

        // update() waiters must survive consent-gated utag loads (>10s), or the
        // identity-carrying appLaunch is lost for the whole session (CBSENTWEB-5126).
        // No clock expiry — shed load only on queue depth, evicting the oldest
        // post-launch-ready event so the freshest session state reaches dataHub.
        if (
            this.pendingUpdateWaiterCount() >= MAX_PENDING_UPDATE_EVENTS &&
            !this.evictOldestEvictableWaiter()
        ) {
            log(
                '[TrackerEngine] update() queue is full of pre-launch-ready events. Dropping event:',
                event.type,
            );
            this.logDroppedEvent(event.type, 'queue-cap-full');
            return;
        }

        const isAvailable = await this.waitForDataHub(Infinity);

        if (!isAvailable) {
            log('[TrackerEngine] dataHub is not available on window. Skipping update() call.');
            this.logDroppedEvent(
                event.type,
                typeof window === 'undefined' ? 'no-window' : 'queue-cap-evicted',
            );
            return;
        }
        this.drainFireOnce(event);
        this._update(event);
    }

    /**
     * Get a snapshot of the current tracking state
     * @param waitMs - How long to wait for dataHub when it is not yet available. Pass 0 for a
     * synchronous best-effort read: per-navigation tracking paths must not stall behind a
     * dataHub that consent hasn't created yet (explicit-consent regions keep it absent until
     * the banner is actioned — PTVWS-6991).
     * @returns Promise resolving to the current state dictionary or empty object if unavailable
     */
    static async snapshot(waitMs: number = DATA_HUB_WAIT_TIMEOUT_MS): Promise<IDictionary> {
        try {
            if (!this.isDataHubAvailable()) {
                const isAvailable = waitMs > 0 ? await this.waitForDataHub(waitMs) : false;

                if (!isAvailable) {
                    log(
                        '[TrackerEngine] dataHub is not available on window. Returning empty snapshot.',
                    );
                    return {};
                }
            }

            return window.dataHub!.snapshot() ?? {};
        } catch (error) {
            logError('[TrackerEngine] Error calling dataHub.snapshot():', error);
            return {};
        }
    }

    /**
     * Get the timeline of all tracking events
     * @returns Array of tracking events or empty array if unavailable
     */
    static timeline(): TrackEventPayload[] {
        if (!this.isDataHubAvailable()) {
            log('[TrackerEngine] dataHub is not available on window. Returning empty timeline.');
            return [];
        }

        try {
            return window.dataHub!.timeline();
        } catch (error) {
            logError('[TrackerEngine] Error calling dataHub.timeline():', error);
            return [];
        }
    }
}
